import React, { useEffect, useRef } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  ChevronRight,
  ChevronDown,
  Square,
  Type,
  AlignLeft,
  Image,
  Play,
  MousePointer,
  Users,
  MessageSquare,
  Grid,
  Layout,
  Rows,
  Columns,
  Star,
  Minus,
  Code,
  DollarSign,
  Mail,
  CheckSquare,
  Box,
  EyeOff,
  GripVertical,
  Link,
} from 'lucide-react';

// Icon mapping for section types
const SECTION_ICONS = {
  'layout-1': Layout,
  'header': Type,
  'paragraph': AlignLeft,
  'image': Image,
  'video': Play,
  'cta': MousePointer,
  'button': MousePointer,
  'testimonials': MessageSquare,
  'features': Grid,
  'team': Users,
  'pricing': DollarSign,
  'custom-html': Code,
  'custom-react': Code,
  'contact-form': Mail,
};

// Icon mapping for component types
const COMPONENT_ICONS = {
  'row': Rows,
  'column': Columns,
  'container': Box,
  'block': Square,
  'text': Type,
  'image': Image,
  'button': MousePointer,
  'icon': Star,
  'spacer': Minus,
  'video': Play,
  'listItem': CheckSquare,
  'link': Link,
};

// Container types that can have children
const CONTAINER_TYPES = ['row', 'column', 'container', 'block'];

/**
 * Get icon for a section type
 */
function getSectionIcon(sectionType, iconProp) {
  // If icon is provided as a component, use it
  if (iconProp && typeof iconProp !== 'string') {
    return iconProp;
  }
  // If icon is a string, try to map it
  if (iconProp && typeof iconProp === 'string') {
    const mapped = SECTION_ICONS[iconProp.toLowerCase()] || COMPONENT_ICONS[iconProp.toLowerCase()];
    if (mapped) return mapped;
  }
  // Fallback to section type mapping
  return SECTION_ICONS[sectionType] || Square;
}

/**
 * Get icon for a component type
 */
function getComponentIcon(componentType) {
  return COMPONENT_ICONS[componentType] || Square;
}

/**
 * Get an array of breakpoints where visibility is set to hidden (false)
 * @param {Object} visibility - The visibility object { base, tablet, desktop }
 * @returns {string[]} Array of hidden breakpoint names
 */
function getHiddenBreakpoints(visibility) {
  if (!visibility) return [];

  const hiddenBreakpoints = [];

  // Check each breakpoint - only add if explicitly false (not null/undefined)
  if (visibility.base === false) {
    hiddenBreakpoints.push('mobile');
  }
  if (visibility.tablet === false) {
    hiddenBreakpoints.push('tablet');
  }
  if (visibility.desktop === false) {
    hiddenBreakpoints.push('desktop');
  }

  return hiddenBreakpoints;
}

/**
 * LayersTreeNode - Individual tree node with expand/collapse
 *
 * Handles:
 * - Section nodes (top level)
 * - Component nodes (nested within layout sections)
 */
export default function LayersTreeNode({
  type, // 'section' | 'component'
  section,
  sectionType,
  component,
  componentType,
  label,
  icon: iconProp,
  isSelected,
  isHovered,
  isExpanded,
  hasChildren,
  depth,
  onToggleExpand,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onContextMenu,
  // New props for drag and drop
  sectionId,
  sectionIndex,
  componentPath,
  parentPath,
  index,
  isDragging,
  isDragActive, // True when any drag operation is in progress
  // Site sections
  siteSections,
  // Rename props
  isRenaming,
  renameValue,
  onRenameChange,
  onRenameSubmit,
  onRenameCancel,
  onStartRename,
}) {
  // Base indent: 12px per depth level
  // Components get extra 16px offset to align with section content (accounting for section drag handle)
  const baseIndent = depth * 12;
  const indent = type === 'component' ? baseIndent + 16 : baseIndent;

  // Determine the icon to use
  const Icon = type === 'section'
    ? getSectionIcon(sectionType, iconProp)
    : getComponentIcon(componentType);

  // Get hidden breakpoints for visibility indicator
  const visibility = type === 'component' ? component?.props?.visibility : section?.props?.visibility;
  const hiddenBreakpoints = getHiddenBreakpoints(visibility);
  const hasHiddenBreakpoints = hiddenBreakpoints.length > 0;

  // Check if this is a container type that can accept children
  const isContainerType = type === 'component' && CONTAINER_TYPES.includes(componentType);
  const childrenCount = component?.children?.length || 0;

  // Draggable setup (for both sections and components)
  const draggableId = type === 'section' && section?.id
    ? `section::${section.id}`
    : type === 'component' && sectionId && componentPath
      ? `${sectionId}.${componentPath}`
      : null;

  const { attributes, listeners, setNodeRef: setDragRef, isDragging: isThisDragging } = useDraggable({
    id: draggableId || 'disabled',
    disabled: !draggableId,
    data: type === 'section'
      ? { type: 'section', sectionId: section?.id }
      : { type: 'component', componentPath, sectionId }
  });

  // Drop zone before this node (for insertion)
  // Using :: as delimiter since section IDs are UUIDs (contain dashes)
  const dropZoneId = type === 'component' && sectionId
    ? `drop::${sectionId}::${parentPath || 'root'}::${index}`
    : null;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: dropZoneId || 'disabled-drop',
    disabled: type !== 'component' || !dropZoneId,
    data: {
      type: 'insertBefore',
      parentPath,
      index,
      sectionId,
    }
  });

  // Drop zone inside container for dropping as last child (only for container types when expanded)
  const childDropZoneId = isContainerType && sectionId && componentPath && isExpanded
    ? `drop::${sectionId}::${componentPath}::${childrenCount}`
    : null;

  const { setNodeRef: setChildDropRef, isOver: isOverChild } = useDroppable({
    id: childDropZoneId || 'disabled-child-drop',
    disabled: !childDropZoneId,
    data: {
      type: 'insertInto',
      parentPath: componentPath,
      index: childrenCount,
      sectionId,
    }
  });

  // Drop zone on the container node itself (for dropping into collapsed/empty containers)
  const containerDropZoneId = isContainerType && sectionId && componentPath && !isExpanded
    ? `drop::${sectionId}::${componentPath}::0`  // Drop as first child
    : null;

  const { setNodeRef: setContainerDropRef, isOver: isOverContainer } = useDroppable({
    id: containerDropZoneId || 'disabled-container-drop',
    disabled: !containerDropZoneId,
    data: {
      type: 'insertInto',
      parentPath: componentPath,
      index: 0,
      sectionId,
    }
  });

  // Auto-expand timer for containers when dragging over them
  const expandTimerRef = useRef(null);

  useEffect(() => {
    // Only start timer if:
    // - A drag is active
    // - This is a container type
    // - We're hovering over this container (via the container drop zone)
    // - Container is not already expanded
    if (isDragActive && isContainerType && isOverContainer && !isExpanded) {
      expandTimerRef.current = setTimeout(() => {
        onToggleExpand?.();
      }, 500); // 500ms delay before auto-expand
    }

    // Clear timer when conditions change
    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    };
  }, [isDragActive, isContainerType, isOverContainer, isExpanded, onToggleExpand]);

  // For sections, render WITH drag capability
  if (type === 'section') {
    return (
      <div
        ref={setDragRef}
        data-layer-id={section?.id ? `section-${section.id}` : undefined}
        {...attributes}
        {...listeners}
        className={`
          group flex items-center h-7 px-2 cursor-grab select-none
          transition-colors duration-75
          ${isSelected
            ? 'bg-blue-50 text-blue-700'
            : isHovered
              ? 'bg-blue-50/50'
              : 'hover:bg-gray-50'
          }
          ${isThisDragging ? 'opacity-50' : ''}
        `}
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onContextMenu={onContextMenu}
      >
        {/* Drag Handle */}
        <div
          className="w-4 flex-shrink-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="w-3 h-3 text-gray-400" />
        </div>

        {/* Expand/Collapse Toggle */}
        <div
          className="w-4 flex-shrink-0 flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          ) : (
            <span className="w-3" />
          )}
        </div>

        {/* Icon */}
        <Icon
          className={`w-3.5 h-3.5 mr-2 flex-shrink-0 ${
            isSelected ? 'text-blue-600' : 'text-gray-400'
          }`}
        />

        {/* Label */}
        {isRenaming ? (
          <input
            type="text"
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onRenameSubmit?.();
              } else if (e.key === 'Escape') {
                onRenameCancel?.();
              }
            }}
            onBlur={() => onRenameSubmit?.()}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="text-xs flex-1 bg-white border border-blue-300 rounded px-1 py-0 outline-none focus:ring-1 focus:ring-blue-400 min-w-0"
          />
        ) : (
          <span
            className={`text-xs truncate flex-1 ${
              isSelected ? 'font-medium' : ''
            }`}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onStartRename?.();
            }}
          >
            {label}
          </span>
        )}

        {/* Site section indicator */}
        {section?.content?.siteSection_id && (
          <span
            className="flex items-center ml-1 text-blue-500"
            title={`Site Section - Changes apply to all pages using "${siteSections?.find(s => s.id === section.content.siteSection_id)?.name || 'this section'}"`}
          >
            <Link className="w-3 h-3" />
          </span>
        )}

        {/* Hidden breakpoints indicator */}
        {hasHiddenBreakpoints && (
          <span
            className="flex items-center ml-1 text-gray-400"
            title={`Hidden on: ${hiddenBreakpoints.join(', ')}`}
          >
            <EyeOff className="w-3 h-3" />
          </span>
        )}

        {/* Type indicator for sections */}
        {sectionType && !isRenaming && (
          <span className="text-[10px] text-gray-400 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {sectionType}
          </span>
        )}
      </div>
    );
  }

  // Combine refs for container types (need both drag and drop on same element)
  const combinedRef = (node) => {
    setDragRef(node);
    if (isContainerType && !isExpanded) {
      setContainerDropRef(node);
    }
  };

  // For components, render with drag/drop
  return (
    <>
      {/* Drop zone before this node */}
      <div
        ref={setDropRef}
        className={`layers-drop-zone h-0.5 mx-2 rounded transition-all duration-150 ${
          isOver ? 'layers-drop-zone-active bg-blue-400 h-1' : 'bg-transparent'
        }`}
        style={{ marginLeft: `${8 + indent}px` }}
      />

      {/* The actual draggable node */}
      <div
        ref={combinedRef}
        data-layer-id={sectionId && componentPath ? `${sectionId}.${componentPath}` : undefined}
        {...attributes}
        {...listeners}
        className={`
          group flex items-center h-7 px-2 cursor-grab select-none
          transition-all duration-150
          ${isSelected
            ? 'bg-blue-50 text-blue-700'
            : isHovered
              ? 'bg-blue-50/50'
              : 'hover:bg-gray-50'
          }
          ${isThisDragging || isDragging ? 'opacity-50' : ''}
          ${isOverContainer ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/30' : ''}
        `}
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onContextMenu={onContextMenu}
      >
        {/* Expand/Collapse Toggle */}
        <div
          className="w-4 flex-shrink-0 flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          ) : (
            <span className="w-3" />
          )}
        </div>

        {/* Icon */}
        <Icon
          className={`w-3.5 h-3.5 mr-2 flex-shrink-0 ${
            isSelected ? 'text-blue-600' : 'text-gray-400'
          }`}
        />

        {/* Label */}
        {isRenaming ? (
          <input
            type="text"
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onRenameSubmit?.();
              } else if (e.key === 'Escape') {
                onRenameCancel?.();
              }
            }}
            onBlur={() => onRenameSubmit?.()}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="text-xs flex-1 bg-white border border-blue-300 rounded px-1 py-0 outline-none focus:ring-1 focus:ring-blue-400 min-w-0"
          />
        ) : (
          <span
            className={`text-xs truncate flex-1 ${
              isSelected ? 'font-medium' : ''
            }`}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onStartRename?.();
            }}
          >
            {label}
          </span>
        )}

        {/* Hidden breakpoints indicator */}
        {hasHiddenBreakpoints && (
          <span
            className="flex items-center ml-1 text-gray-400"
            title={`Hidden on: ${hiddenBreakpoints.join(', ')}`}
          >
            <EyeOff className="w-3 h-3" />
          </span>
        )}
      </div>

      {/* Drop zone inside container for dropping as last child */}
      {isContainerType && isExpanded && (
        <div
          ref={setChildDropRef}
          className={`layers-drop-zone mx-2 rounded transition-all duration-150 ${
            childrenCount === 0
              ? isOverChild
                ? 'bg-blue-100 border-2 border-dashed border-blue-400 h-6'
                : 'bg-gray-50 border border-dashed border-gray-300 h-6'
              : isOverChild
                ? 'layers-drop-zone-active bg-blue-400 h-1'
                : 'bg-transparent h-0.5'
          }`}
          style={{ marginLeft: `${8 + indent + 12}px` }}
        >
          {childrenCount === 0 && (
            <span className="text-[10px] text-gray-400 flex items-center justify-center h-full">
              Drop here
            </span>
          )}
        </div>
      )}
    </>
  );
}
